import type {
  BriefAmendment,
  BriefAttachment,
  BriefComment,
  BriefDetail,
  BriefQuestion,
  DyspatchEvent,
  EnrollmentStep,
  HubHealth,
  HubMetrics,
  HubStats,
  HubUser,
  IntegrationBranch,
  InviteRecord,
  MeDevicesResponse,
  MeSummary,
  OnboardingBriefing,
  PauseMode,
  ProjectBrief,
  ProjectDetail,
  ProjectRollup,
  ReviewResponse,
  Schedule,
  ScheduleDetail,
  ScheduleInput,
  SchedulePreset,
  SchedulePresetsResponse,
  ScheduleSummary,
  ScheduleUpdate,
  SweepReport,
  Task,
  TaskDetail,
  WorkerRecord,
} from '@dyspatch/core';

/**
 * The hub's HTTP surface, typed.
 *
 * Every response shape now comes from `@dyspatch/core`: the stored records were
 * always there, and phase 4b lifted the route-handler projections (`/api/review`,
 * `/api/stats`, `/api/me`, `/api/projects*`) into `core/views.ts` so the route
 * that builds one and the view that renders it are checked against the same
 * declaration. Nothing about a response is declared twice any more.
 */

/** Thrown on 401 so one `catch` in the app can decide to show the login gate. */
export class UnauthorizedError extends Error {
  constructor() {
    super('unauthorized');
    this.name = 'UnauthorizedError';
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The hub answered 403 with `kind: "MustChangePasswordError"`: this account is
 * on a temporary password and nothing but the change will be served. Its own
 * class so `lib/query.ts` can send the person to the card rather than toast the
 * sentence on every panel that happened to be fetching. The hub's own wire name
 * is the discriminator, so the two cannot drift apart silently.
 */
export class PasswordChangeRequiredError extends ApiError {
  constructor(message: string) {
    super(message, 403);
    this.name = 'PasswordChangeRequiredError';
  }
}

const MUST_CHANGE_PASSWORD_KIND = 'MustChangePasswordError';

/**
 * How many tasks `api.tasks()` asks for, and the hub's own cap on that route.
 *
 * It matters to callers because the rows come back *oldest first*: a hub with
 * more tasks than this does not return the newest ones, it returns the first
 * 500 ever created. Anything that counts tasks has to know whether it is looking
 * at the whole set, which is what a full page of rows tells it.
 */
export const TASK_LIST_LIMIT = 500;

const ACTOR_KEY = 'dyspatch.actor';

/**
 * Who the audit log will name **on the legacy path only**. The server-rendered
 * dashboard stored this under the same key and sent it as `x-dyspatch-actor` on
 * every request; the key is kept so a browser that used that page is already
 * identified here.
 *
 * Signing in with an account clears it (`lib/auth.ts`), because the hub ignores
 * the header for a Better Auth session - the session names the person - and a
 * browser should not go on sending a claim nobody reads.
 */
export function storedActor(): string | null {
  return localStorage.getItem(ACTOR_KEY);
}

export function setStoredActor(name: string): void {
  localStorage.setItem(ACTOR_KEY, name.trim());
}

export function clearStoredActor(): void {
  localStorage.removeItem(ACTOR_KEY);
}

/**
 * Who the hub is told made this call, on whatever it is being sent.
 *
 * Only ever set on the legacy admin-token path, where it is the only thing
 * naming a person; it is stored at the gate rather than prompted for
 * mid-flight, which is the rule dashboard.ts settled on. A signed-in browser
 * has nothing stored and sends nothing.
 */
function callerHeaders(source?: HeadersInit): Headers {
  const headers = new Headers(source);
  const actor = storedActor();
  if (actor) headers.set('x-dyspatch-actor', actor);
  return headers;
}

/** The hub's answer, with its refusals turned into the classes above. */
async function readAnswer<T>(response: Response): Promise<T> {
  if (response.status === 401) throw new UnauthorizedError();
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: string;
      kind?: string;
    } | null;
    if (response.status === 403 && body?.kind === MUST_CHANGE_PASSWORD_KIND) {
      throw new PasswordChangeRequiredError(body.error || 'change your password first');
    }
    throw new ApiError(body?.error || response.statusText, response.status);
  }
  return (await response.json()) as T;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = callerHeaders(init.headers);
  if (init.body !== undefined) headers.set('content-type', 'application/json');

  const response = await fetch(path, { credentials: 'same-origin', ...init, headers });
  return readAnswer<T>(response);
}

/**
 * A multipart POST: the same call `request` makes, minus the one header it
 * cannot set here.
 *
 * `request` sets `content-type: application/json` on anything with a body,
 * which on a `FormData` overwrites the `multipart/form-data; boundary=...` the
 * browser computed for the body it is about to frame. The hub then reads a
 * multipart body with no boundary to split it on and finds no parts at all.
 * So the content type is left to `fetch`, and everything else - the caller
 * header, the cookie, the 401, the error body - is shared rather than written
 * twice.
 */
async function upload<T>(path: string, body: FormData): Promise<T> {
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: callerHeaders(),
    body,
  });
  return readAnswer<T>(response);
}

function post<T>(path: string, body: unknown = {}): Promise<T> {
  return request<T>(path, { method: 'POST', body: JSON.stringify(body) });
}

export type { ReviewQueueEntry as ReviewEntry } from '@dyspatch/core';
/** Re-exported so views name the wire shape without importing core twice. */
export type {
  BriefDetail,
  HubHealth,
  HubStats,
  MeDevicesResponse,
  MeSummary,
  ProjectDetail,
  ProjectRollup,
  ReviewResponse,
  Schedule,
  ScheduleDetail,
  SchedulePreset,
  SchedulePresetsResponse,
  ScheduleSummary,
  SweepReport,
  TaskDetail,
};

/**
 * One registered repo, from `GET /api/repos` (issue #101). Not a core type: the
 * table it comes from lives only in `@dyspatch/db`, and importing that here
 * would pull `node:crypto` into the browser bundle for a shape three fields
 * wide.
 */
export interface RepoRow {
  slug: string;
  githubUrl: string | null;
  defaultBranch: string | null;
  enabled: boolean;
  autoFileOnRed: boolean;
  /**
   * The project half of standing instructions: what every agent run in this
   * repo is told, on top of the fleet's. Null when nothing is set, which is
   * different from an empty string only in that nobody has been here yet.
   */
  instructions: string | null;
  systemPrompt: string | null;
  announceOnReady: boolean;
  /**
   * What the repo's own `.dyspatch.json` said when the hub last read it.
   *
   * Null, with a null `dyspatchCheckedAt`, means the hub has never been able
   * to look - no GitHub token, or GitHub refused - which is a different fact
   * from `absent` and must not be drawn as one.
   */
  dyspatchState: 'present' | 'absent' | 'unreadable' | null;
  dyspatchGates: string[] | null;
  dyspatchCheckedAt: string | null;
  addedBy: string;
  createdAt: string;
}

/** The fleet half, one row for the hub. Null until an admin writes one. */
export interface FleetPromptRow {
  instructions: string | null;
  systemPrompt: string | null;
  updatedBy: string;
  updatedAt: string;
}

/** What both editors post. Blank is a value: it clears the field. */
export interface AgentPromptInput {
  instructions: string;
  systemPrompt: string;
}

/** One person, with their devices and account window, from `GET /api/users`. */
export interface PersonRow extends HubUser {
  devices: {
    id: string;
    name: string;
    hostname?: string | null;
    presence: string;
    deviceHeadroom: number;
    headroomSource: string;
    activeTaskCount: number;
    maxConcurrency: number;
    lastHeartbeatAt?: string | null;
  }[];
  account: {
    headroom: number;
    load: number;
    maxConcurrentTasks: number;
    deviceCount: number;
  } | null;
}

export const api = {
  /**
   * The legacy path: trades the admin token for the HttpOnly cookie `EventSource`
   * can carry. Better Auth's own endpoints are spoken to through its client in
   * `lib/auth.ts`, not from here.
   */
  login: (token: string) => post<{ ok: true }>('/session', { token }),
  logout: () => request<{ ok: true }>('/session', { method: 'DELETE' }),

  // Reads ---------------------------------------------------------------
  me: () => request<MeSummary>('/api/me'),
  myDevices: () => request<MeDevicesResponse>('/api/me/devices'),
  /**
   * Take a machine of mine out of the fleet. Its token stops working, which is
   * how you stop a device you no longer trust. Scoped by ownership: the hub
   * answers 404 for a device that is not yours. Refused, like the admin verb,
   * while it still holds unfinished work.
   */
  retireMyDevice: (workerId: string) =>
    request<{ retired: WorkerRecord }>(`/api/me/devices/${workerId}`, { method: 'DELETE' }),
  /**
   * Connect a laptop of my own. Needs no admin: the code is minted for the
   * caller's own address, so the device lands on their account and nothing
   * else. Returns the steps to run and the code inside them.
   */
  connectMyDevice: (name?: string) =>
    post<{
      invite: InviteRecord;
      code: string;
      steps: EnrollmentStep[];
    }>('/api/me/devices', name ? { name } : {}),
  /**
   * What a person needs on their laptop before a device can do anything: the
   * fleet's identity policy, the environment variables the tool catalog wants,
   * and the repos this hub has registered. Every one of those was configured
   * on the hub, enforced on the worker, and said nowhere a person could read
   * it. Variable names only; the catalog holds no values.
   */
  onboarding: () => request<OnboardingBriefing>('/api/onboarding'),
  /**
   * What an enrollment link's code implies: the device name it will take, when
   * it expires, and the steps with the code already in them. Reads nothing and
   * spends nothing, so a link opened twice is the same link.
   */
  enrollmentSteps: (code: string) =>
    request<{
      workerName: string;
      expiresAt: string;
      status: 'open' | 'used' | 'expired' | 'revoked';
      steps: EnrollmentStep[];
    }>(`/enroll/steps?code=${encodeURIComponent(code)}`),
  myTasks: (limit = 200) => request<{ tasks: Task[] }>(`/api/me/tasks?limit=${limit}`),
  myBriefs: (limit = 100) => request<{ briefs: ProjectBrief[] }>(`/api/me/briefs?limit=${limit}`),
  myEvents: (limit = 60) => request<{ events: DyspatchEvent[] }>(`/api/me/events?limit=${limit}`),

  health: () => request<HubHealth>('/health'),
  stats: () => request<HubStats>('/api/stats'),
  /**
   * The window view, optionally narrowed to one project or one person. The hub
   * applies the filter as a projection on the rows before it reduces them, so
   * every figure in the response is the slice rather than only the handful that
   * carry a per-repo tally. A repo can contain characters a query string cares
   * about and a person is an email, so both go through `encodeURIComponent`.
   */
  metrics: (days: number, filter?: { repo?: string | null; person?: string | null }) => {
    const query = [`days=${days}`];
    if (filter?.repo) query.push(`repo=${encodeURIComponent(filter.repo)}`);
    if (filter?.person) query.push(`person=${encodeURIComponent(filter.person)}`);
    return request<HubMetrics>(`/api/metrics?${query.join('&')}`);
  },
  review: () => request<ReviewResponse>('/api/review'),
  workers: () => request<{ workers: WorkerRecord[] }>('/api/workers'),
  briefs: (limit = 200) => request<{ briefs: ProjectBrief[] }>(`/api/briefs?limit=${limit}`),
  brief: (id: string) => request<BriefDetail>(`/api/briefs/${id}`),
  tasks: (limit = TASK_LIST_LIMIT) => request<{ tasks: Task[] }>(`/api/tasks?limit=${limit}`),
  task: (id: string) => request<TaskDetail>(`/api/tasks/${id}`),
  events: (limit = 60) => request<{ events: DyspatchEvent[] }>(`/api/events?limit=${limit}`),
  projects: () => request<{ projects: ProjectRollup[] }>('/api/projects'),
  project: (repo: string) => request<ProjectDetail>(`/api/projects/${encodeURIComponent(repo)}`),
  /** Every registered repo, enabled or not. Any signed-in caller: the job composer's picker reads this too. */
  repos: () => request<{ repos: RepoRow[] }>('/api/repos'),
  users: () => request<{ users: PersonRow[] }>('/api/users'),
  /**
   * Every invite, not only the ones still open.
   *
   * The route filters to open by default, which left People unable to answer the
   * question an admin actually has after minting a code: did they enroll. A
   * redeemed invite simply disappeared, and so did an expired one, so "nothing
   * here" meant both "it worked" and "it never happened". The page filters the
   * list itself, so the open section is unchanged and the closed ones have
   * somewhere to be.
   *
   * The limit is raised with the filter. The route's default is 100 rows
   * newest-first, which was every open invite on any hub and is now a hundred
   * mostly-closed ones: on a hub that has enrolled a lot of devices the open
   * invites would start falling off the end of the list that counts them.
   */
  invites: () => request<{ invites: InviteRecord[] }>('/api/invites?all=true&limit=300'),

  /**
   * Schedules: the jobs that file themselves (docs/JOB-LIFECYCLE.md 2.7). The
   * list drops each template, which is a whole task graph; the detail carries
   * it, plus every job the schedule has filed.
   */
  schedules: () => request<{ schedules: ScheduleSummary[] }>('/api/schedules'),
  schedule: (id: string) => request<ScheduleDetail>(`/api/schedules/${id}`),
  /** Starting points for the composer; today, the nightly as a schedule row. */
  schedulePresets: () => request<SchedulePresetsResponse>('/api/schedules/presets'),

  // Writes --------------------------------------------------------------
  // Every one of these is a route the old dashboard already called, with the
  // same body. The actor rides in the header `request()` sets, which is what
  // makes the audit line name a person rather than "admin".
  fileBrief: (input: {
    title: string;
    goal: string;
    repos: string[];
    submittedBy: string;
    /** Handed to the planning agent verbatim; the hub defaults them to none. */
    constraints?: string[];
    deadline?: string | null;
  }) => post<{ brief: ProjectBrief }>('/api/briefs', input),
  /**
   * Attach one file to a job: a screenshot of the bug, the mockup it should
   * look like, a spec PDF. One call per file, because the hub refuses a whole
   * multipart body when any part of it is over a limit, and a person who
   * attached four files should not lose the three that were fine.
   *
   * The field name is `file`, which is the only one the route reads.
   */
  uploadJobFile: (briefId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return upload<{ attachments: BriefAttachment[] }>(
      `/api/briefs/${encodeURIComponent(briefId)}/attachments`,
      form,
    );
  },
  deleteJobFile: (briefId: string, attachmentId: string) =>
    request<{ removed: BriefAttachment }>(
      `/api/briefs/${encodeURIComponent(briefId)}/attachments/${encodeURIComponent(attachmentId)}`,
      { method: 'DELETE' },
    ),
  /**
   * Where the bytes are. A path rather than a fetch: this is what an `href` or
   * an `<img src>` points at, and the cookie rides along with it the same way
   * it does on every other same-origin request.
   */
  jobFileUrl: (briefId: string, attachmentId: string) =>
    `/api/briefs/${encodeURIComponent(briefId)}/attachments/${encodeURIComponent(attachmentId)}`,
  /**
   * Ask for a plan. The hub files one synthetic PLAN task on the brief and moves
   * it to `decomposing`; the actor comes from the session, not the body. Legal
   * only on a draft with no tasks yet, which is what `awaitsPlan` mirrors so the
   * verb is not offered where the hub answers 409.
   */
  requestPlan: (id: string) => post<{ brief: ProjectBrief; task: Task }>(`/api/briefs/${id}/plan`),
  /**
   * Issue #26: check the job's branches combined. Files one synthetic CHECK
   * step whose product is a verdict over the union of the open branches; a
   * worker merges them into a scratch branch it never pushes and runs the
   * repo's gates on the result. Re-runnable, because the answer goes stale the
   * moment anything merges.
   */
  checkJob: (id: string) =>
    post<{ task: Task; branches: IntegrationBranch[] }>(`/api/briefs/${id}/check`),
  /**
   * The Go button. Task-targeted comments fold into their specs server-side.
   *
   * No name goes up with either of these. The hub records the credential that
   * made the call, so a name in the body would be a claim it ignores.
   */
  approveBrief: (id: string) =>
    post<{ brief: ProjectBrief; tasks: Task[] }>(`/api/briefs/${id}/approve`),
  rejectBrief: (id: string, reason?: string) =>
    post<{ brief: ProjectBrief }>(`/api/briefs/${id}/reject`, { reason }),
  /**
   * The pull cord on an approved brief: every task of it that has not finished is
   * cancelled and the brief closes. The reason is required, because it is the only
   * record of why work already running was stopped.
   */
  cancelBrief: (id: string, reason: string) =>
    post<{ brief: ProjectBrief; tasks: Task[]; skipped: number }>(`/api/briefs/${id}/cancel`, {
      reason,
    }),
  commentOnBrief: (id: string, body: string, taskKey?: string) =>
    post<unknown>(`/api/briefs/${id}/comments`, { body, taskKey }),
  /**
   * Settling one of the planner's open questions (docs/PLAN-QUESTIONS.md).
   *
   * Not a gate and not an approval: the plan was approvable with every question
   * open, and this only decides what the agent is told. The hub files the answer
   * as a note on each affected step, so it folds into those specs at Go the same
   * way every other plan comment does.
   */
  answerQuestion: (id: string, key: string, answer: string) =>
    post<{ question: BriefQuestion; comments: BriefComment[] }>(
      `/api/briefs/${id}/questions/${encodeURIComponent(key)}/answer`,
      { answer },
    ),
  /**
   * Pause and resume a whole job (docs/JOB-LIFECYCLE.md 2.5). `checkpoint`
   * stops every running step on its device's next heartbeat and keeps the
   * work on its branch; `finish` lets the current attempts end and holds the
   * steps after that. Submitter or admin, the same set that may approve.
   */
  pauseBrief: (id: string, mode: PauseMode, reason?: string) =>
    post<{ brief: ProjectBrief; tasks: Task[]; skipped: number }>(`/api/briefs/${id}/pause`, {
      mode,
      reason,
    }),
  resumeBrief: (id: string) =>
    post<{ brief: ProjectBrief; tasks: Task[] }>(`/api/briefs/${id}/resume`),
  /**
   * Amend an approved (or finished) job: a new goal, added rules, added
   * steps. Added steps arrive as drafts and wait for approval; nothing here
   * dispatches (docs/JOB-LIFECYCLE.md 2.6).
   */
  amendBrief: (id: string, amendment: BriefAmendment) =>
    post<{ brief: ProjectBrief; tasks: Task[] }>(`/api/briefs/${id}/amend`, amendment),
  /**
   * A note on one task, at whatever phase it is in (docs/JOB-LIFECYCLE.md 2.2).
   * The hub decides the phase from the task's status and refuses (409) once the
   * task is over; `lib/notes.ts` mirrors that so the box says what will happen.
   */
  noteOnTask: (taskId: string, body: string) =>
    post<{ note: BriefComment }>(`/api/tasks/${taskId}/notes`, { body }),
  /** File an agent finding as a labelled GitHub issue, through the hub's filer. */
  fileFindingAsIssue: (taskId: string, noteId: string) =>
    post<{ note: BriefComment; alreadyFiled: boolean }>(
      `/api/tasks/${taskId}/notes/${noteId}/issue`,
    ),

  /**
   * Records that a person merged the PR. The hub re-reads GitHub first and
   * answers 409 if GitHub disagrees at that moment (issue #125); `force` is
   * the person overruling that, which they are entitled to do - the hub has no
   * merge permission, so this has always been a report of something they did.
   */
  markDone: (taskId: string, options: { force?: boolean } = {}) =>
    post<{ task: Task }>(`/api/tasks/${taskId}/done`, options),
  /**
   * Files an urgent task to fix a reviewed pull request that has gone red or
   * started conflicting. A NEW task, not a retry: see `fix-pr.ts` on the hub.
   */
  fixPr: (taskId: string) =>
    post<{ task: Task; fixes: string; state: string }>(`/api/tasks/${taskId}/fix-pr`),
  /** Send back with feedback, reassign elsewhere, or plainly retry a failure. */
  requeueTask: (taskId: string, options: { feedback?: string; reassign?: boolean } = {}) =>
    post<{ task: Task }>(`/api/tasks/${taskId}/requeue`, options),
  cancelTask: (taskId: string, reason?: string) =>
    post<{ task: Task }>(`/api/tasks/${taskId}/cancel`, { reason }),
  pauseTask: (taskId: string, mode: PauseMode, reason?: string) =>
    post<{ task: Task }>(`/api/tasks/${taskId}/pause`, { mode, reason }),
  resumeTask: (taskId: string) => post<{ task: Task }>(`/api/tasks/${taskId}/resume`),
  /** Rewrite the spec of a step that has not started. The hub refuses once a device holds it. */
  setTaskSpec: (taskId: string, spec: string) =>
    post<{ task: Task }>(`/api/tasks/${taskId}/spec`, { spec }),

  setDnd: (workerId: string, dnd: boolean) =>
    post<{ worker: WorkerRecord }>(`/api/workers/${workerId}/dnd`, { dnd }),
  /**
   * Retire a device: the row and its token hash go, and that laptop stops being
   * part of the fleet. Refused by the hub while the device still holds an
   * unfinished task, and the refusal names the keys.
   */
  retireWorker: (workerId: string) =>
    request<{ retired: WorkerRecord }>(`/api/workers/${workerId}`, { method: 'DELETE' }),
  /**
   * Add a person: the domain row and the sign-in account in one call. The
   * password is generated by the hub and is in this response and nowhere else,
   * exactly as `setTemporaryPassword`'s is. Refused when the email already has
   * an account, because that case wants a temporary password instead.
   */
  createPerson: (input: { email: string; name?: string; isAdmin?: boolean }) =>
    post<{ user: HubUser; password: string; mustChangePassword: true }>('/api/users', input),
  /**
   * Register a repo. Admin-only on the server, which checks the slug against
   * GitHub before accepting it (issue #101): a typo is refused here rather than
   * discovered by a job no worker ever picks up.
   */
  createRepo: (slug: string) => post<{ repo: RepoRow }>('/api/repos', { slug }),
  /**
   * Read a repo's `.dyspatch.json` again. The registration's reading is a cache
   * of something that changes in the repo rather than in the hub, so a row
   * saying "no gates" about a repo that has had gates for a month is worse than
   * saying nothing.
   */
  recheckRepo: (slug: string) =>
    post<{ repo: RepoRow }>(`/api/repos/${encodeURIComponent(slug)}/recheck`, {}),
  setRepoEnabled: (slug: string, enabled: boolean) =>
    post<{ repo: RepoRow }>(`/api/repos/${encodeURIComponent(slug)}/enabled`, { enabled }),
  /**
   * A project's standing instructions, system prompt and announce switch, in
   * one write. All three go together because the editor is one form: a partial
   * post would make an emptied textarea mean "leave it alone".
   */
  setRepoPrompt: (slug: string, input: AgentPromptInput & { announceOnReady: boolean }) =>
    post<{ repo: RepoRow }>(`/api/repos/${encodeURIComponent(slug)}/prompt`, input),
  /** The fleet half of the same thing. Null when nobody has set one. */
  fleetPrompt: () => request<{ prompt: FleetPromptRow | null }>('/api/settings/agent-prompt'),
  setFleetPrompt: (input: AgentPromptInput) =>
    post<{ prompt: FleetPromptRow }>('/api/settings/agent-prompt', input),
  setLimits: (userId: string, maxConcurrentTasks: number) =>
    post<{ user: HubUser }>(`/api/users/${userId}/limits`, {
      maxConcurrentTasks,
    }),
  /** Promote or demote. The same route as the limit, and admin-only on the server. */
  setRole: (userId: string, isAdmin: boolean) =>
    post<{ user: HubUser }>(`/api/users/${userId}/limits`, { isAdmin }),
  /** Refused by the hub while they own a device or an open invite, and on yourself. */
  removeMember: (userId: string) =>
    request<{ removed: HubUser }>(`/api/users/${userId}`, { method: 'DELETE' }),
  /**
   * A temporary password for somebody who has lost theirs. The password is in
   * this response and nowhere else: the hub journals that it happened, never
   * what it was. Their sessions are closed and they must change it on sign-in.
   * Admin-only on the server; refused for yourself and for a person who has
   * never signed in.
   */
  setTemporaryPassword: (userId: string) =>
    post<{ user: HubUser; password: string; mustChangePassword: true }>(
      `/api/users/${userId}/password`,
    ),
  createInvite: (input: { workerName: string; forEmail: string; repos: string[] }) =>
    post<{ invite: InviteRecord; code: string; steps: EnrollmentStep[]; url: string }>(
      '/api/invites',
      input,
    ),
  revokeInvite: (id: string) => post<{ invite: InviteRecord }>(`/api/invites/${id}/revoke`),
  /**
   * One dyspatch sweep, synchronously, and the report of what it did. This is
   * the dispatch scheduler, not the intake adapters: it offers queued work to
   * free devices, expires offers nobody acked and reaps runs that ran over. The
   * hub runs it on a timer anyway, so the button is for the minute somebody is
   * watching a queue and does not want to wait for the next tick.
   */
  sweep: () => post<{ report: SweepReport }>('/api/sweep'),

  /**
   * Making a schedule is approving its plan for every run it will ever make, so
   * the approver is the credential rather than anything in this body, exactly as
   * pressing Go on a job is. Changing one is a fresh approval by whoever changed
   * it; pausing, resuming, running and deleting are the approver's or an
   * admin's, and the hub says so rather than the console hiding the button.
   */
  createSchedule: (input: ScheduleInput) =>
    post<{ schedule: Schedule; cadence: string }>('/api/schedules', input),
  updateSchedule: (id: string, patch: ScheduleUpdate) =>
    post<{ schedule: Schedule; cadence: string }>(`/api/schedules/${id}`, patch),
  pauseSchedule: (id: string, reason?: string) =>
    post<{ schedule: ScheduleSummary; alreadyPaused: boolean }>(`/api/schedules/${id}/pause`, {
      reason,
    }),
  resumeSchedule: (id: string) =>
    post<{ schedule: ScheduleSummary; alreadyRunning: boolean }>(`/api/schedules/${id}/resume`),
  /**
   * The same firing the clock makes, now. It still skips when the last job it
   * filed is open, and the answer says which job it is waiting on, because two
   * runs of one schedule at once is the thing skip-if-open exists to prevent.
   */
  runSchedule: (id: string) =>
    post<{
      report: { outcome: string; briefId: string | null; reason?: string; nextRunAt: string };
      schedule: ScheduleSummary | null;
    }>(`/api/schedules/${id}/run`),
  deleteSchedule: (id: string) =>
    request<{ removed: ScheduleSummary }>(`/api/schedules/${id}`, { method: 'DELETE' }),
};
